import {FC, ReactNode, RefObject} from "react"

import {Styles, css, cx} from "@styled-system/css"
import {hstack} from "@styled-system/patterns"

interface Props {
  value: string
  maxLength?: number
  placeholder?: string
  onFocus?: () => void
  onChange: (value: string) => void
  onEnter?: () => void
  disabled?: boolean
  inputRef?: RefObject<HTMLTextAreaElement | null>
  leftComponent?: ReactNode
  rightComponent?: ReactNode
  className?: string
  /**
   * When true, renders a 2px animated linear-gradient border (Repair Guru
   * blue→cyan palette) plus a soft cyan drop shadow, matching the Figma
   * "Smart Animate" chat input. The gradient rotates on a 4s loop.
   */
  animatedGradientBorder?: boolean
  /**
   * Pin left/right slots to the row center (e.g. fixed-height widget bar).
   * Default keeps them bottom-aligned for multi-line textarea growth.
   */
  centerSideActions?: boolean
}

const GRADIENT_BORDER_CLASS = "ri2-gradient-border-input"
const GRADIENT_BORDER_CSS = `
  @keyframes ri2GradientBorderShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .${GRADIENT_BORDER_CLASS} {
    position: relative !important;
    border: 0 !important;
    border-radius: 10px !important;
    background-color: #FFFFFF !important;
    background-clip: padding-box !important;
    box-shadow: 0 4px 24px rgba(0, 225, 255, 0.40) !important;
  }
  .${GRADIENT_BORDER_CLASS}::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 2px;
    background: linear-gradient(
      90deg,
      #70EEFF 0%,
      #00A9D7 25%,
      #91CCF0 50%,
      #00E1FF 75%,
      #015DA0 100%
    );
    background-size: 300% 300%;
    animation: ri2GradientBorderShift 4s linear infinite;
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    pointer-events: none;
  }
`

export const InputField: FC<Props> = ({
  value,
  maxLength,
  placeholder,
  onFocus,
  onChange,
  onEnter,
  disabled,
  inputRef,
  leftComponent,
  rightComponent,
  className,
  animatedGradientBorder = false,
  centerSideActions = false,
}) => {
  const onFocusTextArea = (): void => {
    inputRef?.current?.focus()
  }

  const sideSlotClass = (side: "left" | "right"): string =>
    hstack(
      centerSideActions
        ? {
            alignItems: "center",
            height: "auto",
            marginY: 0,
            minHeight: 0,
            alignSelf: "center",
            ...(side === "left" ? {marginRight: 8} : {}),
          }
        : {
            alignItems: "flex-end",
            height: "100%",
            marginY: 0,
            minHeight: 29,
            alignSelf: "end",
            ...(side === "left" ? {marginRight: 8} : {}),
          },
    )

  const sharedTextAreaStyles: Styles = {
    gridArea: "1 / 1 / 2 / 2",
    margin: 0,
    padding: 0,
    border: "none",
    outline: "none",
    flexGrow: 1,
    width: "100%",
    wordBreak: "break-word",
    backgroundColor: "none",
    appearance: "none",
    lineHeight: "20px",
    maxHeight: 100,
  }

  return (
    <div
      className={cx(
        css({
          display: "flex",
          flexDirection: "row",
          borderRadius: 12,
          backgroundColor: "#FFFFFF",
          borderColor: "border.brand.secondarySolid",
          borderWidth: 2,
          borderStyle: "solid",
          backdropFilter: "blur(15px)",
          boxSizing: "border-box",
          alignItems: "center",
          cursor: "text",
          padding: {
            base: "14px 12px 14px 12px",
            desktop: "16px 20px 16px 20px",
          },
          transition: "box-shadow ease-in 0.2s",
          "&:focus-within": {
            boxShadow:
              "0px 0px 0px 6px token(colors.background.brand.secondary)",
          },
        }),
        animatedGradientBorder && GRADIENT_BORDER_CLASS,
        className,
      )}
      onClick={onFocusTextArea}
    >
      {animatedGradientBorder && <style>{GRADIENT_BORDER_CSS}</style>}
      {leftComponent && (
        <div className={sideSlotClass("left")}>{leftComponent}</div>
      )}
      <div
        className={css({
          display: "inline-grid",
          alignItems: "stretch",
          flexGrow: 1,
          width: "100%",
          outline: "none",
        })}
      >
        <textarea
          data-test-id="chat-input"
          rows={1}
          maxLength={maxLength}
          value={value}
          placeholder={placeholder}
          ref={inputRef}
          onFocus={onFocus}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event): void => {
            if (event.key === "Enter" && !event.shiftKey && onEnter) {
              event.preventDefault()

              if (!disabled) {
                onEnter()
              }
            }
          }}
          className={css(sharedTextAreaStyles, {
            resize: "none",
            overflow: "hidden",
            textStyle: "bodyLight",
            "&::placeholder": {
              color: "text.quaternary",
            },
          })}
        />
        <div
          className={css(sharedTextAreaStyles, {
            whiteSpace: "pre-wrap",
            visibility: "hidden",
          })}
        >
          {value}{" "}
        </div>
      </div>

      {rightComponent && (
        <div className={sideSlotClass("right")}>{rightComponent}</div>
      )}
    </div>
  )
}
